import { dispatchAuthFailure } from '../utils/auth-failure';

type MessageHandler = (data: unknown) => void;

/**
 * v0.79.27 (Bug 2 hotfix) — REMOVED the 8s connect-attempt watchdog
 * that v0.79.26 introduced. Verified on real iPhone Safari WebView via
 * Layer 2 diag (b2_diag_iphone_ws.py): cold-start WS handshakes on
 * iOS Safari can take 8+ seconds (TLS pool warm-up + apparent serialized
 * parallel-WS handshake limit), so the 8s timeout was force-closing
 * connections that would have opened naturally in another 1-3 seconds.
 *
 * v0.79.28 (Bug 2 follow-up) — TWO holes closed after live-fire user
 * report (multi-captain lobby chat broken across backend restarts):
 *
 * (1) `maxReconnectAttempts = 10` silent give-up REMOVED. After ~3 min
 *     of exp-backoff retries, the prior code returned silently from
 *     scheduleReconnect; banner stayed «Переподключение…» forever and
 *     other players stopped seeing the user's lobby chat (their server-
 *     side lobbySessions entry was gone). Now retries continue forever
 *     (capped at 30s delay). Server restarts and network blips are
 *     ALWAYS recoverable.
 *
 * (2) `window.addEventListener('online', forceReconnect)` ADDED. Browser
 *     knows when network comes back; previously we ignored that signal
 *     and only reconnected on visibility/pageshow. Foreground tabs
 *     during Wi-Fi flap / VPN reconnect now auto-recover without bg/fg
 *     cycle.
 *
 * Recovery on resume is now driven by `forceReconnect()` from THREE
 * independent triggers: visibility/pageshow handlers in views, the
 * `online` event listener installed here, and the cap-removed
 * scheduleReconnect chain that retries indefinitely.
 */

export class ReconnectingWebSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private handlers = new Map<string, Set<MessageHandler>>();
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;
  private onStatusChange: (connected: boolean) => void;
  private onlineHandler: (() => void) | null = null;

  constructor(url: string, onStatusChange: (connected: boolean) => void) {
    this.url = url;
    this.onStatusChange = onStatusChange;
    // v0.79.28 — install a single `online` listener for the lifetime of
    // this socket. Browser fires this when navigator.onLine flips false→
    // true; we treat it as a forceReconnect signal. Removed in close().
    if (typeof window !== 'undefined') {
      this.onlineHandler = () => this.forceReconnect();
      window.addEventListener('online', this.onlineHandler);
    }
  }

  connect(): void {
    this.intentionallyClosed = false;
    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.onStatusChange(true);
    };

    this.ws.onmessage = (event) => {
      // Split error paths: a JSON-parse failure is a server/network bug;
      // a handler throw is a frontend bug. Lumping them under one catch
      // hid the `spectator-view` msg-shape regression for every release
      // before Apr 2026 — the TypeError looked identical to a parse error
      // in the console ("WS parse error:"). Route them separately so future
      // structural mismatches surface as actionable "WS handler error" logs.
      let msg: { type?: string };
      try {
        msg = JSON.parse(event.data as string);
      } catch (err) {
        console.error('WS parse error:', err, event.data);
        return;
      }
      const type = typeof msg?.type === 'string' ? msg.type : '';
      const dispatch = (h: MessageHandler, handlerType: string) => {
        try {
          h(msg);
        } catch (err) {
          console.error(`WS handler error [${handlerType}]:`, err, msg);
        }
      };
      const typeHandlers = this.handlers.get(type);
      if (typeHandlers) typeHandlers.forEach(h => dispatch(h, type));
      // Wildcard handlers observe every message — a single handler failure
      // shouldn't starve the others.
      const wildcardHandlers = this.handlers.get('*');
      if (wildcardHandlers) wildcardHandlers.forEach(h => dispatch(h, '*'));
    };

    this.ws.onclose = (e: CloseEvent) => {
      // v0.79.18 — close 1008 = backend rejected auth (stale/invalid token).
      // Don't retry — that's a permanent failure. Dispatch event so main.ts
      // handler can clearAllGameData + redirect to /join. Setting
      // intentionallyClosed=true blocks scheduleReconnect.
      if (e.code === 1008) {
        this.intentionallyClosed = true;
        dispatchAuthFailure(`ws-1008:${e.reason || ''}`);
        this.onStatusChange(false);
        return;
      }
      if (!this.intentionallyClosed) {
        this.onStatusChange(false);
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  on(type: string, handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler);
    return () => this.handlers.get(type)?.delete(handler);
  }

  send(data: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  close(): void {
    this.intentionallyClosed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    // v0.79.28 — symmetric teardown of the `online` listener installed
    // in the constructor. Without this, every closed socket would leave
    // a dead handler bound to the global window event, slowly leaking
    // listeners across view mount/unmount cycles.
    if (this.onlineHandler && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    this.ws?.close();
    this.ws = null;
  }

  /**
   * /investigate 2026-05-02 — Bug 2 (iPhone Safari resume → stuck banner).
   *
   * Force the WS into a fresh connect attempt regardless of where the
   * current state machine sits. iOS Safari can leave a ws in CONNECTING
   * after bg/fg without firing onopen/onerror in a timely manner; the
   * onclose path may also arrive late. Visibility/pageshow handlers in
   * views (lobby/chat/location/spectator) call this to sidestep the
   * stuck state on resume.
   *
   * No-op if the user is in the middle of an intentional close (leaving
   * the game / auth-1008 logout) — those paths must NOT auto-resurrect
   * a socket the caller deliberately tore down.
   *
   * Detaches handlers from the old socket before close() so its onclose
   * doesn't double-fire scheduleReconnect against this fresh attempt.
   * Resets reconnectAttempts so the user gets a fresh exponential-backoff
   * window — they just came back, they shouldn't pay penalty for a
   * bg-window that exhausted retries silently.
   */
  forceReconnect(): void {
    if (this.intentionallyClosed) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    if (this.ws) {
      try {
        this.ws.onopen = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
        this.ws.close();
      } catch { /* ignore close errors */ }
      this.ws = null;
    }
    this.connect();
  }

  private scheduleReconnect(): void {
    // v0.79.28 — REMOVED the maxReconnectAttempts=10 cap. The prior
    // silent give-up (after ~3 min of exp-backoff) left foreground
    // users stranded after every backend restart and network blip.
    // Cap-on-delay (30s max) is enough rate limiting; server restarts
    // are ALWAYS recoverable, so retry forever.
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }
}
